import { useState, useRef, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { categories } from '@/mocks/categories';

export default function Navbar() {
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 40);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    setMobileOpen(false);
    setDropdownOpen(false);
  }, [location]);

  return (
    <nav
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled
          ? 'bg-white/95 backdrop-blur-lg border-b border-background-200/70'
          : 'bg-white/85 backdrop-blur-md'
      }`}
      style={scrolled ? {} : { boxShadow: '0 1px 8px rgba(0,0,0,0.04)' }}
    >
      <div className="w-full px-4 md:px-10">
        <div className="flex items-center justify-between h-16 md:h-18">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2 shrink-0">
            <span className="text-lg md:text-2xl font-heading font-bold tracking-tight text-foreground-950">
              Kakobuy<span className="text-primary-500">Sheet</span>
            </span>
          </Link>

          {/* Desktop nav links */}
          <div className="hidden md:flex items-center gap-7">
            <Link to="/" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
              Home
            </Link>

            {/* Categories Dropdown */}
            <div className="relative" ref={dropdownRef}>
              <button
                onClick={() => setDropdownOpen(!dropdownOpen)}
                className="flex items-center gap-1.5 text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap cursor-pointer"
              >
                Categories
                <i className={`ri-arrow-down-s-line text-base transition-transform duration-200 ${dropdownOpen ? 'rotate-180' : ''}`}></i>
              </button>

              {dropdownOpen && (
                <div
                  className="absolute top-full left-1/2 -translate-x-1/2 mt-3 w-[640px] bg-white rounded-xl border border-background-200/70 py-5 px-2 z-50"
                  style={{ boxShadow: '0 8px 32px rgba(0,0,0,0.08)' }}
                >
                  <div className="grid grid-cols-2 gap-1 max-h-[480px] overflow-y-auto px-3">
                    {categories.map((cat) => (
                      <a
                        key={cat.slug}
                        href={cat.url}
                        target="_blank"
                        rel="noopener nofollow"
                        className="flex items-center gap-3 px-3 py-3 rounded-lg hover:bg-background-50 transition-colors group cursor-pointer"
                      >
                        <div className="w-10 h-10 rounded-lg overflow-hidden shrink-0 bg-background-100">
                          <img
                            src={cat.image}
                            alt={cat.name}
                            className="w-full h-full object-cover object-top"
                            loading="lazy"
                          />
                        </div>
                        <span className="text-sm font-semibold text-foreground-800 group-hover:text-primary-600 transition-colors whitespace-nowrap">
                          {cat.name}
                        </span>
                      </a>
                    ))}
                  </div>
                </div>
              )}
            </div>

            <Link to="/articles/kakobuy-spreadsheet-guide" className="text-sm font-semibold text-foreground-900 hover:text-primary-500 transition-colors whitespace-nowrap">
              Shopping Guide
            </Link>
          </div>

          {/* Right CTAs */}
          <div className="flex items-center gap-2.5">
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="hidden sm:inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-secondary-500 text-background-50 text-sm font-semibold hover:bg-secondary-600 transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-file-list-3-line"></i>
              Kakobuy Spreadsheet
            </a>
            <a
              href="https://kaspreadsheet.com/"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 px-4 py-2 rounded-full bg-primary-500 text-background-50 text-sm font-semibold hover:bg-primary-600 transition-all hover:-translate-y-0.5 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-shopping-bag-3-line"></i>
              Buy It Now
            </a>

            {/* Mobile hamburger */}
            <button
              className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-foreground-900 hover:bg-background-100 transition-colors cursor-pointer"
              onClick={() => setMobileOpen(!mobileOpen)}
              aria-label="Toggle menu"
            >
              <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
            </button>
          </div>
        </div>

        {/* Mobile menu panel */}
        {mobileOpen && (
          <div className="md:hidden pb-5 border-t border-background-200/70 mt-2 pt-4">
            <Link
              to="/"
              className="flex items-center gap-2 py-3 px-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 rounded-lg hover:bg-background-50 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-home-4-line text-base"></i> Home
            </Link>
            <Link
              to="/articles/kakobuy-spreadsheet-guide"
              className="flex items-center gap-2 py-3 px-2 text-sm font-semibold text-foreground-900 hover:text-primary-500 rounded-lg hover:bg-background-50 transition-colors"
              onClick={() => setMobileOpen(false)}
            >
              <i className="ri-book-read-line text-base"></i> Shopping Guide
            </Link>

            <p className="py-3 px-2 text-xs font-bold text-foreground-400 uppercase tracking-widest mt-2">
              Categories
            </p>
            <div className="grid grid-cols-2 gap-1">
              {categories.map((cat) => (
                <a
                  key={cat.slug}
                  href={cat.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2.5 px-2 py-2.5 rounded-lg hover:bg-background-50 transition-colors group cursor-pointer"
                >
                  <div className="w-8 h-8 rounded-md overflow-hidden shrink-0 bg-background-100">
                    <img
                      src={cat.image}
                      alt={cat.name}
                      className="w-full h-full object-cover object-top"
                      loading="lazy"
                    />
                  </div>
                  <span className="text-xs font-medium text-foreground-800 group-hover:text-primary-600 transition-colors leading-tight">
                    {cat.name}
                  </span>
                </a>
              ))}
            </div>

            <div className="mt-4 flex gap-2 px-2">
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-secondary-500 text-background-50 text-xs font-semibold hover:bg-secondary-600 transition-all cursor-pointer"
              >
                <i className="ri-file-list-3-line"></i>
                Kakobuy Spreadsheet
              </a>
              <a
                href="https://kaspreadsheet.com/"
                target="_blank"
                rel="noopener nofollow"
                className="flex-1 flex items-center justify-center gap-1.5 py-2.5 rounded-full bg-primary-500 text-background-50 text-xs font-semibold hover:bg-primary-600 transition-all cursor-pointer"
              >
                <i className="ri-shopping-bag-3-line"></i>
                Buy It Now
              </a>
            </div>
          </div>
        )}
      </div>
    </nav>
  );
}